import React from 'react';
import { Box } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomPagination from '@ui-components/Table/CustomPagination';
import EditOnlyIcon from '@assets/images/editOnly.svg';
import type { Clinician } from '../../API/services/ClinicianService';
import FacilityList from '@assets/images/facilityList.svg';
import CustomLink from '@qler-view/shared_lib/src/shared/lib/ui-components/Link/CustomLink';
import { hasRole } from '../login/storageUtility';
interface ClinicianTableProps {
  isLoading: boolean;
  clinicians: Clinician[];
  totalRecords: number;
  page: number;
  rowsPerPage: number;
  sortField: string;
  sortType: 'asc' | 'desc';
  action?: string;

  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onEdit?: (id: number) => void;
  onFacilityCount?: (id: number, name: string) => void;

  getSortIcon: (type: 'asc' | 'desc') => React.ReactNode;
  getStartItem: (page: number, size: number) => number;
  getEndItem: (page: number, size: number, total: number) => number;
  onView?: (id: number) => void;
}
const ClinicianTable: React.FC<ClinicianTableProps> = ({
  isLoading,
  clinicians,
  totalRecords,
  page,
  rowsPerPage,
  sortField,
  sortType,
  action,
  onSort,
  onPageChange,
  onEdit,
  onFacilityCount,
  getSortIcon,
  getStartItem,
  getEndItem,
  onView,
}) => {
  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CustomTypography id="loading" variant="h5" color="textDisabled">
            Loading...
          </CustomTypography>
        </Box>
      ) : (
        <CustomTable id="table-clinicians-list">
          <CustomTableHead id="table-clinicians-list-header">
            <CustomTableRow id="table-clinicians-list-header-row">
              <CustomTableCell
                id="table-clinicians-providerId"
                onClick={() => onSort('providerBusinessId')}
              >
                USER ID&nbsp;
                {sortField === 'providerBusinessId' && getSortIcon(sortType)}
              </CustomTableCell>

              <CustomTableCell
                id="table-clinicians-firstName"
                onClick={() => onSort('firstName')}
              >
                FIRST NAME&nbsp;
                {sortField === 'firstName' && getSortIcon(sortType)}
              </CustomTableCell>

              <CustomTableCell
                id="table-clinicians-lastName"
                onClick={() => onSort('lastName')}
              >
                LAST NAME&nbsp;
                {sortField === 'lastName' && getSortIcon(sortType)}
              </CustomTableCell>

              <CustomTableCell
                id="table-clinicians-primary-email"
                onClick={() => onSort('primaryEmail')}
              >
                PRIMARY EMAIL&nbsp;
                {sortField === 'primaryEmail' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-clinicians-primary-phone"
                onClick={() => onSort('primaryPhone')}
              >
                PRIMARY PHONE&nbsp;
                {sortField === 'primaryPhone' && getSortIcon(sortType)}
              </CustomTableCell>

              <CustomTableCell
                id="table-clinicians-zoomUserKey"
                onClick={() => onSort('zoomUserKey')}
              >
                ZOOM USER ID&nbsp;
                {sortField === 'zoomUserKey' && getSortIcon(sortType)}
              </CustomTableCell>

              {/* <CustomTableCell
                id="table-clinicians-createdOn"
                onClick={() => onSort('createdOn')}
              >
                CREATED ON&nbsp;
                {sortField === 'createdOn' && getSortIcon(sortType)}
              </CustomTableCell> */}
              <CustomTableCell
                id="table-clinicians-status"
                onClick={() => onSort('status')}
              >
                STATUS&nbsp;
                {sortField === 'status' && getSortIcon(sortType)}
              </CustomTableCell>
              {action === 'list' && (
                <CustomTableCell id="table-clinicians-actions">
                  ACTIONS
                </CustomTableCell>
              )}
              {hasRole(['ops-admin']) && action === 'mapping' && (
                <CustomTableCell id="table-clinicians-actions">
                  ACTIONS
                </CustomTableCell>
              )}
            </CustomTableRow>
          </CustomTableHead>

          {clinicians.length > 0 ? (
            <CustomTableBody id="table-clinicians-list-body">
              {clinicians.map((clinician) => (
                <CustomTableRow
                  id="table-clinicians-list-body-row"
                  key={clinician.id}
                >
                  <CustomTableCell id="table-clinicians-provider-business-Id">
                    <CustomLink
                      id={`link-clinician-id-${clinician.id}`}
                      color="primary"
                      onClick={() => onView?.(clinician.id)}
                    >
                      {clinician.providerBusinessId}
                    </CustomLink>
                  </CustomTableCell>
                  <CustomTableCell id="table-clinicians-firstName">
                    {clinician.firstName || 'N/A'}
                  </CustomTableCell>
                  <CustomTableCell id="table-clinicians-lastName">
                    {clinician.lastName || 'N/A'}
                  </CustomTableCell>
                  <CustomTableCell id="table-clinicians-primary-email">
                    {clinician.primaryEmail || 'N/A'}
                  </CustomTableCell>
                  <CustomTableCell id="table-clinicians-primary-phone">
                    {clinician.primaryPhone || 'N/A'}
                  </CustomTableCell>

                  <CustomTableCell id="table-clinicians-zoomUserKey">
                    {clinician.zoomUserKey || 'N/A'}
                  </CustomTableCell>
                  {/* <CustomTableCell id="table-clinicians-createdOn">
                    {moment(clinician.createdOn).format('MM-DD-YYYY HH:mm')}
                  </CustomTableCell> */}
                  <CustomTableCell id="table-clinicians-status">
                    {clinician.status || 'N/A'}
                  </CustomTableCell>
                  {action === 'list' && (
                    <CustomTableCell
                      id="table-clinicians-actions"
                      className="width-10"
                    >
                      <Box className="flex align-items-center">
                        <Box
                          className="flex align-items-center"
                          onClick={() =>
                            onFacilityCount?.(
                              clinician.id,
                              `${clinician.firstName ?? ''} ${clinician.lastName ?? ''}`.trim()
                            )
                          }
                        >
                          <CustomIconButton
                            id="user-icon"
                            size="small"
                            icon={<FacilityList />}
                            onClick={() => {}}
                          />
                          <Box className="display-count cursor-pointer">
                            {clinician.associatedFacilityCount}
                          </Box>
                        </Box>
                        {/* <Box className="ml-16">
                          <CustomIconButton
                            id="table-clinicians-edit-button"
                            size="small"
                            icon={<ViewIcon />}
                            onClick={() => onView?.(clinician.id)}
                          />
                        </Box> */}
                        {hasRole(['ops-admin']) && (
                          <Box className="ml-16">
                            <CustomIconButton
                              id="table-clinicians-edit-button"
                              size="small"
                              icon={<EditOnlyIcon />}
                              onClick={() => onEdit?.(clinician.id)}
                            />
                          </Box>
                        )}
                      </Box>
                    </CustomTableCell>
                  )}
                  {hasRole(['ops-admin']) && action === 'mapping' && (
                    <CustomTableCell id="table-clinicians-actions">
                      <CustomIconButton
                        id="table-clinicians-edit-button"
                        size="small"
                        icon={<EditOnlyIcon />}
                        onClick={() => onEdit?.(Number(clinician.id))}
                      />
                    </CustomTableCell>
                  )}
                </CustomTableRow>
              ))}
            </CustomTableBody>
          ) : (
            <CustomTableBody id="table-clinicians-list-body">
              <CustomTableRow id="table-clinicians-list-body-row">
                <CustomTableCell
                  id="table-clinicians-list-body-row-empty"
                  colspan={7}
                >
                  <Box className="flex flex-justify-center align-items-center">
                    <CustomTypography
                      id="no-clinicians-found"
                      variant="body2"
                      color="textDisabled"
                    >
                      No Clinicians Found
                    </CustomTypography>
                  </Box>
                </CustomTableCell>
              </CustomTableRow>
            </CustomTableBody>
          )}
        </CustomTable>
      )}

      {totalRecords > 0 && (
        <Box className="mt-32">
          <Grid2 container className="align-items-center">
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Box className="flex justify-content-start">
                <CustomTypography
                  id="showing-clinicians"
                  variant="body2"
                  color="textDisabled"
                >
                  Showing {getStartItem(page, rowsPerPage)} –{' '}
                  {getEndItem(page, rowsPerPage, totalRecords)} of{' '}
                  {totalRecords} Clinicians
                </CustomTypography>
              </Box>
            </Grid2>
            {totalRecords > rowsPerPage && (
              <Grid2 size={{ xs: 12, md: 8 }}>
                <Box display="flex" justifyContent="end">
                  <CustomPagination
                    id="table-clinicians-pagination"
                    count={Math.ceil(totalRecords / rowsPerPage)}
                    page={page + 1}
                    onPageChange={(_, value) => onPageChange(value - 1)}
                  />
                </Box>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}
    </>
  );
};

export default ClinicianTable;
